import { useLayoutEffect, useRef, useState } from 'react';
import { useMenuInput } from './useMenuInput';

export interface MenuNavHandlers {
  /**
   * Called with the `data-nav-id` of the focused element on CONFIRM. Return
   * `false` to signal a denied action — the central interface-sound dispatch
   * then plays the error cue instead of the confirm one (see useInput.ts).
   */
  onConfirm?: (id: string) => void | boolean;
  onCancel?: () => void;
  onPause?: () => void;
  onTabNext?: () => void;
  onTabPrev?: () => void;
  /**
   * Fired on the `SECONDARY` action (Y / KeyT). Contextual semantics: toggle
   * details on Loadout, transfer a stack to the chest, etc. Receives the
   * focused element's `data-nav-id`. Return `false` for a denied action.
   */
  onSecondary?: (id: string | null) => void | boolean;
  /**
   * Fired on the `TERTIARY` action (Select / KeyV). Additional contextual
   * semantics (e.g. sort inventories). Return `false` for a denied action.
   */
  onTertiary?: (id: string | null) => void | boolean;
  /**
   * Fired on the `QUATERNARY` action (X / KeyR). Fourth contextual action
   * (e.g. empty the inventory). Return `false` for a denied action.
   */
  onQuaternary?: (id: string | null) => void | boolean;
  /**
   * Fired on the `DETAILS` action (Start / KeyH). Toggles a detailed panel —
   * uniform semantics across screens that expose an optional detail view.
   */
  onDetails?: (id: string | null) => void | boolean;
  /**
   * Fired on Left / Right **only** when the focused row has a single column
   * (typical of a slider). On multi-column rows the arrows keep navigating
   * between columns.
   */
  onLeft?: (id: string) => void;
  onRight?: (id: string) => void;
  /**
   * Fired on Right when focus is on the **last** column of a multi-column row,
   * instead of wrapping to the first column. Opt-in — omit to keep wrapping.
   * Receives the focused element's `data-nav-id` (or null).
   */
  onEdgeRight?: (id: string | null) => void;
  /** Symmetric to `onEdgeRight` for Left on the **first** column. */
  onEdgeLeft?: (id: string | null) => void;
}

export interface MenuNavOptions extends MenuNavHandlers {
  /** Disable all navigation input (e.g. during key capture). */
  disabled?: boolean;
  /** Freeze directional movement but still fire confirm / cancel / pause / tabs. */
  frozen?: boolean;
  /** CSS class applied to the focused element. Default: `'nav-focused'`. */
  focusClass?: string;
}

export interface MenuNavResult {
  /** Ref to attach to the navigable container element. */
  containerRef: React.RefObject<HTMLDivElement>;
  /** `data-nav-id` of the currently focused element, or `null`. */
  focusedId: string | null;
  focusedRow: number;
  focusedCol: number;
  setFocus: (row: number, col?: number) => void;
}

interface GridInfo {
  /** Map `"row,col"` → HTMLElement */
  cells: Map<string, HTMLElement>;
  /** Sorted unique row numbers present in the DOM. */
  rows: number[];
  /** For each row, sorted unique column numbers. */
  colsPerRow: Map<number, number[]>;
}

const EMPTY_GRID: GridInfo = {
  cells: new Map(),
  rows: [],
  colsPerRow: new Map(),
};

function scanGrid(container: HTMLElement): GridInfo {
  const nodes = container.querySelectorAll<HTMLElement>('[data-nav-row]');
  const cells = new Map<string, HTMLElement>();
  const colSets = new Map<number, Set<number>>();

  nodes.forEach((el) => {
    const row = parseInt(el.dataset.navRow!, 10);
    const col = parseInt(el.dataset.navCol ?? '0', 10);
    cells.set(`${row},${col}`, el);
    if (!colSets.has(row)) {
      colSets.set(row, new Set());
    }
    colSets.get(row)!.add(col);
  });

  const rows = Array.from(colSets.keys()).sort((a, b) => a - b);
  const colsPerRow = new Map<number, number[]>();
  colSets.forEach((s, row) => {
    colsPerRow.set(
      row,
      Array.from(s).sort((a, b) => a - b),
    );
  });

  return { cells, rows, colsPerRow };
}

function nearestCol(available: number[], target: number): number {
  let best = available[0];
  let dist = Math.abs(best - target);
  for (const v of available) {
    const d = Math.abs(v - target);
    if (d < dist) {
      best = v;
      dist = d;
    }
  }
  return best;
}

function findScrollParent(
  el: HTMLElement,
  root: HTMLElement,
): HTMLElement | null {
  let cur = el.parentElement;
  while (cur && cur !== root) {
    const { overflowY } = getComputedStyle(cur);
    if (overflowY === 'auto' || overflowY === 'scroll') {
      return cur;
    }
    cur = cur.parentElement;
  }
  if (root) {
    const { overflowY } = getComputedStyle(root);
    if (overflowY === 'auto' || overflowY === 'scroll') {
      return root;
    }
  }
  return null;
}

/**
 * Declarative menu navigation hook.
 *
 * Scans a container for elements annotated with **data attributes** and
 * handles directional navigation, focus‑class management, and scrolling
 * automatically.
 *
 * ### Data attributes
 * | Attribute | Required | Description |
 * |---|---|---|
 * | `data-nav-row` | ✔ | Row index (number). |
 * | `data-nav-col` | ✘ | Column index (number, default `0`). |
 * | `data-nav-id`  | ✘ | Identifier passed to `onConfirm`. |
 *
 * Row / column numbers need not be contiguous — only their sort order
 * matters for computing neighbours.  The hook applies `focusClass`
 * (default `"nav-focused"`) to the currently focused element after every
 * React commit, so CSS rules based on that class always stay in sync.
 */
export function useMenuNavigation(options: MenuNavOptions): MenuNavResult {
  const containerRef = useRef<HTMLDivElement>(null);
  const gridRef = useRef<GridInfo>(EMPTY_GRID);
  const [focus, setFocusState] = useState({ row: 0, col: 0 });
  const prevFocusRef = useRef({ row: -1, col: -1 });
  // Tracks the `focusedId` value emitted on the last render. The layout effect
  // compares it to the freshly scanned grid to detect a stale derivation.
  const renderedIdRef = useRef<string | null>(null);
  const [, forceResolveFocus] = useState(0);
  const cls = options.focusClass ?? 'nav-focused';

  // Keep a fresh reference to options for callbacks inside useMenuInput.
  const optRef = useRef(options);
  optRef.current = options;

  // Depless: re-scan the grid and re-apply the focus class after every commit.
  // The `forceResolveFocus` call is guarded (fires only when the resolved id
  // differs from the rendered one), so it cannot loop despite the missing deps.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useLayoutEffect(() => {
    const c = containerRef.current;
    if (!c) {
      return;
    }

    const info = scanGrid(c);
    gridRef.current = info;

    c.querySelectorAll(`.${cls}`).forEach((el) => el.classList.remove(cls));

    const el = info.cells.get(`${focus.row},${focus.col}`);
    if (el) {
      el.classList.add(cls);

      // Scroll only when focus actually moved.
      if (
        focus.row !== prevFocusRef.current.row ||
        focus.col !== prevFocusRef.current.col
      ) {
        const scrollParent = findScrollParent(el, c);
        if (scrollParent) {
          // Margin so section headers / titles above the element stay visible
          // (sticky table headers + section labels, as in Options > Controls).
          const MARGIN = 110;
          const pRect = scrollParent.getBoundingClientRect();
          const eRect = el.getBoundingClientRect();

          if (eRect.top - MARGIN < pRect.top) {
            scrollParent.scrollBy({
              top: eRect.top - pRect.top - MARGIN,
              behavior: 'auto',
            });
          } else if (eRect.bottom + MARGIN > pRect.bottom) {
            scrollParent.scrollBy({
              top: eRect.bottom - pRect.bottom + MARGIN,
              behavior: 'auto',
            });
          }
        } else {
          el.scrollIntoView({ block: 'nearest', behavior: 'auto' });
        }
      }
    }
    prevFocusRef.current = { row: focus.row, col: focus.col };

    // The render-time `focusedId` is derived from the *previous* commit's grid;
    // on mount (or when the grid restructures) it can lag behind the DOM. If the
    // freshly scanned grid yields a different id than what we just rendered,
    // re-render once so consumers see the resolved value. Guarded → no loop.
    const resolvedId =
      info.cells.get(`${focus.row},${focus.col}`)?.dataset.navId ?? null;
    if (resolvedId !== renderedIdRef.current) {
      forceResolveFocus((t) => t + 1);
    }
  });

  const focusedEl = gridRef.current.cells.get(`${focus.row},${focus.col}`);
  const focusedId = focusedEl?.dataset.navId ?? null;
  renderedIdRef.current = focusedId;

  useMenuInput(
    {
      up: () => {
        if (optRef.current.frozen) {
          return;
        }
        const { rows, colsPerRow } = gridRef.current;
        if (!rows.length) {
          return;
        }
        setFocusState((p) => {
          const i = rows.indexOf(p.row);
          const nr = rows[i <= 0 ? rows.length - 1 : i - 1];
          const avail = colsPerRow.get(nr) ?? [0];
          return {
            row: nr,
            col: avail.includes(p.col) ? p.col : nearestCol(avail, p.col),
          };
        });
      },
      down: () => {
        if (optRef.current.frozen) {
          return;
        }
        const { rows, colsPerRow } = gridRef.current;
        if (!rows.length) {
          return;
        }
        setFocusState((p) => {
          const i = rows.indexOf(p.row);
          const nr = rows[i >= rows.length - 1 ? 0 : i + 1];
          const avail = colsPerRow.get(nr) ?? [0];
          return {
            row: nr,
            col: avail.includes(p.col) ? p.col : nearestCol(avail, p.col),
          };
        });
      },
      left: () => {
        if (optRef.current.frozen) {
          return;
        }
        const avail = gridRef.current.colsPerRow.get(focus.row) ?? [0];
        if (avail.length <= 1) {
          const id = gridRef.current.cells.get(`${focus.row},${focus.col}`)
            ?.dataset.navId;
          if (id) {
            optRef.current.onLeft?.(id);
          }
          return;
        }
        if (focus.col === avail[0] && optRef.current.onEdgeLeft) {
          const id =
            gridRef.current.cells.get(`${focus.row},${focus.col}`)?.dataset
              .navId ?? null;
          optRef.current.onEdgeLeft(id);
          return;
        }
        setFocusState((p) => {
          const a = gridRef.current.colsPerRow.get(p.row) ?? [0];
          const i = a.indexOf(p.col);
          return { ...p, col: a[i <= 0 ? a.length - 1 : i - 1] };
        });
      },
      right: () => {
        if (optRef.current.frozen) {
          return;
        }
        const avail = gridRef.current.colsPerRow.get(focus.row) ?? [0];
        if (avail.length <= 1) {
          const id = gridRef.current.cells.get(`${focus.row},${focus.col}`)
            ?.dataset.navId;
          if (id) {
            optRef.current.onRight?.(id);
          }
          return;
        }
        if (
          focus.col === avail[avail.length - 1] &&
          optRef.current.onEdgeRight
        ) {
          const id =
            gridRef.current.cells.get(`${focus.row},${focus.col}`)?.dataset
              .navId ?? null;
          optRef.current.onEdgeRight(id);
          return;
        }
        setFocusState((p) => {
          const a = gridRef.current.colsPerRow.get(p.row) ?? [0];
          const i = a.indexOf(p.col);
          return { ...p, col: a[i >= a.length - 1 ? 0 : i + 1] };
        });
      },
      confirm: () => {
        const el = gridRef.current.cells.get(`${focus.row},${focus.col}`);
        const id = el?.dataset.navId;
        if (id) {
          return optRef.current.onConfirm?.(id);
        }
      },
      cancel: () => optRef.current.onCancel?.(),
      unpause: () => optRef.current.onPause?.(),
      tabNext: () => optRef.current.onTabNext?.(),
      tabPrev: () => optRef.current.onTabPrev?.(),
      secondary: () => {
        const el = gridRef.current.cells.get(`${focus.row},${focus.col}`);
        return optRef.current.onSecondary?.(el?.dataset.navId ?? null);
      },
      tertiary: () => {
        const el = gridRef.current.cells.get(`${focus.row},${focus.col}`);
        return optRef.current.onTertiary?.(el?.dataset.navId ?? null);
      },
      quaternary: () => {
        const el = gridRef.current.cells.get(`${focus.row},${focus.col}`);
        return optRef.current.onQuaternary?.(el?.dataset.navId ?? null);
      },
      details: () => {
        const el = gridRef.current.cells.get(`${focus.row},${focus.col}`);
        return optRef.current.onDetails?.(el?.dataset.navId ?? null);
      },
    },
    options.disabled,
  );

  const setFocus = (row: number, col = 0) => setFocusState({ row, col });

  return {
    containerRef,
    focusedId,
    focusedRow: focus.row,
    focusedCol: focus.col,
    setFocus,
  };
}
